'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HelpfulButton from 'marketplace-ui-reviews/components/HelpfulButton';
import ReviewActionsWrapper from 'marketplace-ui-reviews/components/ReviewActionsWrapper';
import ReportButton from 'marketplace-ui-reviews/components/ReportButton';
import ReportReviewModal from 'marketplace-ui-reviews/components/modals/ReportReviewModal';
import { Reportables } from 'marketplace-ui-reviews/constants/ReportReasons';
import { reportReview, reportReply, setReviewHelpful } from '../../../actions/ReviewsActions';
import { getIsCustomer, getUserId } from '../../../selectors/auth';
import { makeGetUserMarkedHelpfulSelector } from '../../../selectors/reviews';

const withReportModalState = WrappedComponent => props => {
  const {
    review
  } = props;
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const dispatch = useDispatch();
  const isCustomer = useSelector(getIsCustomer);
  const userId = useSelector(getUserId);
  const getUserMarkedHelpful = useMemo(makeGetUserMarkedHelpfulSelector, []);
  const userMarkedHelpful = useSelector(state => getUserMarkedHelpful(state, review.get('id')));

  const onReviewModalOpen = () => setReviewModalOpen(true);

  const onReplyModalOpen = () => setReplyModalOpen(true);

  const onReviewModalClose = () => setReviewModalOpen(false);

  const onReplyModalClose = () => setReplyModalOpen(false);

  const buildReportModal = useCallback((type, onConfirm, onClose) => /*#__PURE__*/_jsx(ReportReviewModal, {
    review: review,
    type: type,
    onClose: onClose,
    onConfirm: onConfirm
  }), [review]);

  const onReviewModalConfirm = ({
    reason,
    comment
  }) => {
    dispatch(reportReview({
      reviewId: review.get('id'),
      reportReason: reason,
      comment
    }));
    onReviewModalClose();
  };

  const onReplyModalConfirm = ({
    reason,
    comment
  }) => {
    dispatch(reportReply({
      reviewId: review.get('id'),
      replyId: review.getIn(['reply', 'id']),
      reportReason: reason,
      comment
    }));
    onReplyModalClose();
  };

  const reportReviewModal = buildReportModal(Reportables.REVIEW, onReviewModalConfirm, onReviewModalClose);
  const reportReplyModal = buildReportModal(Reportables.REPLY, onReplyModalConfirm, onReplyModalClose);
  const reviewsActionsList = useMemo(() => /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
    className: "vr-hidden",
    children: [/*#__PURE__*/_jsx(HelpfulButton, {
      className: "m-right-2",
      disabled: !isCustomer,
      helpfulCount: review.getHelpfulCount(),
      onClickHelpful: () => dispatch(setReviewHelpful(review.get('id'), userId, !userMarkedHelpful)),
      tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.loginRequired"
      }),
      userMarkedHelpful: userMarkedHelpful
    }), /*#__PURE__*/_jsx(ReportButton, {
      className: "m-left-2",
      isCustomer: isCustomer,
      onClick: onReviewModalOpen
    })]
  }), [isCustomer, review, userId, userMarkedHelpful, dispatch]);
  return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, props, {
    isCustomer: isCustomer,
    onReviewModalOpen: onReviewModalOpen,
    onReplyModalOpen: onReplyModalOpen,
    reviewModalOpen: reviewModalOpen,
    replyModalOpen: replyModalOpen,
    reportReviewModal: reportReviewModal,
    reportReplyModal: reportReplyModal,
    reviewActionsList: reviewsActionsList
  }));
};

export default withReportModalState;